/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'
import SVGMouth from './SVGMouth'
import { useDispatch, useSelector } from 'react-redux'
import { updateAppointment } from '../../../../../Redux/appointments/appointmentSlice'
import { updatePatient } from '../../../../../Redux/patients/patientsSlice'
import { addNewAppointment } from '../../../../../Redux/appointments/appointmentSlice'
import { ReactComponent as HeartSVG } from '../../../../../assets/icons/heart-icon.svg'
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit-icon.svg'

const OrganData = ({ t, patientId, currentOperation, allOperations = [], patientRoom, setCurrentOperation, showGroupedTeeth, setShowGroupedTeeth }) => {
  const dispatch = useDispatch()
  const [selectedType, setSelectedType] = useState('all')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [localTeethList, setLocalTeethList] = useState([])
  const [groupedTeethList, setGroupedTeethList] = useState([])
  const [localContent, setLocalContent] = useState({
    feedback: '',
    therapy: '',
    ekg: '',
    dg: '',
    xray: '',
    spo: '',
    ta: '',
    rtg_pa_pulmo: '',
    spo2: '',
    main_issue: '',
    examination: ''
  })
  
  const feedbackRef = React.useRef(null)
  const therapyRef = React.useRef(null)
  const ekgRef = React.useRef(null)
  const dgRef = React.useRef(null)
  const xrayRef = React.useRef(null)
  const spoRef = React.useRef(null)
  const taRef = React.useRef(null)
  const rtgRef = React.useRef(null)
  const spo2Ref = React.useRef(null)
  const mainIssueRef = React.useRef(null)
  const examinationRef = React.useRef(null)
  
  const selectedOperation = currentOperation || allOperations[0] || {}

  // Get patient data from Redux store
  const fullDataPatient = useSelector((state) => 
    state.patients.patients.find(patient => patient.id === patientId)
  )

  // Initialize local state when selectedOperation or fullDataPatient changes
  useEffect(() => {
    if (selectedOperation) {
      setLocalTeethList(selectedOperation.teeth_list || [])
      setLocalContent({
        feedback: selectedOperation.feedback || '',
        therapy: selectedOperation.therapy || '',
        ekg: fullDataPatient?.ekg || '',
        dg: fullDataPatient?.dg || '',
        xray: fullDataPatient?.xray || '',
        spo: fullDataPatient?.spo || '',
        ta: fullDataPatient?.ta || '',
        rtg_pa_pulmo: fullDataPatient?.rtg_pa_pulmo || '',
        spo2: fullDataPatient?.spo2 || '',
        main_issue: selectedOperation.main_issue || selectedOperation.description || '',
        examination: selectedOperation.examination || ''
      })
    }
  }, [selectedOperation, fullDataPatient])

  // Handle showing grouped teeth
  useEffect(() => {
    if (showGroupedTeeth && showGroupedTeeth.appointments) {
      // Collect all teeth from all appointments in the group
      const allTeeth = showGroupedTeeth.appointments.reduce((teeth, appointment) => {
        if (appointment.teeth_list && Array.isArray(appointment.teeth_list)) {
          return [...teeth, ...appointment.teeth_list]
        }
        return teeth
      }, [])
      
      // Remove duplicates and sort
      const uniqueTeeth = [...new Set(allTeeth)].sort((a, b) => a - b)
      setGroupedTeethList(uniqueTeeth)
    } else {
      setGroupedTeethList([])
    }
  }, [showGroupedTeeth])

  // Check if patient is from stomatology
  const isStomatologyPatient = selectedOperation.category === 'stomatology' || window.location.pathname?.includes('stomatology')
  // Check if operation is a 'termin' (appointment)
  const isTermin = selectedOperation.type === 'termin' || selectedOperation.category === 'termin';

  // If no operation is selected and no grouped teeth to show, show empty state
  if (!currentOperation && groupedTeethList.length === 0) {
    return (
      <div className={styles.organ_data_main_container}>
        <div className={styles.header_container}>
          <div className={styles.header}>
            <h3 className={styles.text}>{t('pt_medical_file')}</h3>
          </div>
        </div>
        <div className={styles.empty_state}>
          <div className={styles.empty_state_icon}>📋</div>
          <h4>{t('no_service_selected')}</h4>
          <p>{t('select_service_to_view_details')}</p>
        </div>
      </div>
    )
  }

  const onToothclick = (toothNumber) => {
    // Only allow tooth selection when editing
    if (!isEditing) return;
    
    setLocalTeethList(prevTeeth => {
      if (prevTeeth.includes(toothNumber)) {
        return prevTeeth.filter(tooth => tooth !== toothNumber)
      } else {
        return [...prevTeeth, toothNumber]
      }
    })
  }

  const handleContentChange = (field, value) => {
    if (!isEditing) return;
    setLocalContent(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      
      // Create the form object with the correct field names
      const formObject = {
        ...localContent,
        teeth_list: localTeethList
      };

      // Check if any changes were made
      const changesHappened = Object.keys(formObject).some((key) => {
        const currentValue = selectedOperation[key] || '';
        const newValue = formObject[key] || '';
        return currentValue !== newValue;
      });

      if (!changesHappened) {
        return;
      }

      // Update patient data in Redux store for all patients
      await dispatch(updatePatient({
        id: patientId,
        main_issue: localContent.main_issue,
        ekg: localContent.ekg,
        dg: localContent.dg,
        xray: localContent.xray,
        spo: localContent.spo,
        ta: localContent.ta,
        rtg_pa_pulmo: localContent.rtg_pa_pulmo,
        spo2: localContent.spo2
      }));

      // Create the complete appointment object with all required fields
      const updatedAppointment = {
        ...selectedOperation,
        feedback: localContent.feedback,
        therapy: localContent.therapy,
        main_issue: localContent.main_issue,
        teeth_list: localTeethList,
        examination: localContent.examination,
        // Ensure these required fields are present
        date: selectedOperation.date || new Date().toISOString().split('T')[0],
        from: selectedOperation.from || 8,
        to: selectedOperation.to || 8.5,
        fromStr: selectedOperation.fromStr || "08:00",
        toStr: selectedOperation.toStr || "08:30",
        category: selectedOperation.category || 'stomatology',
        patient_id: patientId,
        operation_id: selectedOperation.operation_id || '',
        operation_name: selectedOperation.operation_name || '',
        doctor_id: selectedOperation.doctor_id || '',
        doctor_name: selectedOperation.doctor_name || '',
        unit_multiplier: selectedOperation.unit_multiplier || 1,
        array_of_payments: selectedOperation.array_of_payments || [],
        price: selectedOperation.price || 0,
        is_paid: selectedOperation.is_paid || false,
        is_ongoing: selectedOperation.is_ongoing || false,
        color: selectedOperation.color || '#000000'
      };

      // Update the appointment in Firebase and Redux
      const result = await dispatch(updateAppointment(updatedAppointment)).unwrap();
      
      // Update the parent component's state with the new data
      if (setCurrentOperation) {
        setCurrentOperation(result);
      }
      
      // Exit editing mode after successful save
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving medical data:', err);
      setError(err.message || 'Failed to save medical data');
    } finally {
      setIsSaving(false);
    }
  }

  const handleSaveAsNew = async () => {
    try {
      setIsSaving(true)
      setError(null)
      
      console.log('Starting save as new process...');
      
      // Create a new appointment with today's date using the current form data
      const today = new Date().toISOString().split('T')[0];
      
      // Check if patient is from pulmonology
      const isPulmonologyPatient = selectedOperation?.category === 'pulmonology' || 
                                   fullDataPatient?.category === 'pulmonology' ||
                                   window.location.pathname?.includes('pulmonology');
      
      const newAppointmentData = {
        date: today,
        patient_id: patientId,
        patient_name: fullDataPatient?.full_name || '',
        operation_id: selectedOperation?.operation_id || '',
        operation_name: selectedOperation?.operation_name || '',
        name: selectedOperation?.operation_name || selectedOperation?.name || '', // Ensure name field is not undefined
        doctor_id: selectedOperation?.doctor_id || '',
        doctor_name: selectedOperation?.doctor_name || '',
        category: selectedOperation?.category || 'stomatology',
        main_issue: localContent.main_issue || selectedOperation?.main_issue || '',
        feedback: localContent.feedback || selectedOperation?.feedback || '',
        therapy: localContent.therapy || selectedOperation?.therapy || '',
        examination: localContent.examination || selectedOperation?.examination || '',
        teeth_list: localTeethList || selectedOperation?.teeth_list || [],
        room: patientRoom || selectedOperation?.room || '',
        price: selectedOperation?.price || 0,
        service_cost: selectedOperation?.service_cost || 0,
        patient_pay: selectedOperation?.service_cost || selectedOperation?.price || 0, // Set patient_pay equal to service_cost for pulmonology
        difference: 0, // No difference for pulmonology (fully paid)
        unit_multiplier: selectedOperation?.unit_multiplier || 1,
        color: selectedOperation?.color || '#000000',
        from: 8,
        to: 8.5,
        fromStr: "08:00",
        toStr: "08:30",
        is_ongoing: false,
        is_paid: isPulmonologyPatient, // Set as paid for pulmonology patients
        array_of_payments: [],
        creator: '', // Will be set by the addNewAppointment function
        payment_date: isPulmonologyPatient ? today : null // Set payment date to today for pulmonology
      };
      
      console.log('Creating new appointment with data:', newAppointmentData);
      
      // Clean the data to remove any undefined values
      const cleanAppointmentData = Object.fromEntries(
        Object.entries(newAppointmentData).filter(([_, value]) => value !== undefined)
      );
      
      console.log('Cleaned appointment data:', cleanAppointmentData);
      
      // Create the new appointment directly
      const newAppointmentResult = await dispatch(addNewAppointment({
        ...cleanAppointmentData,
        close: () => {} // Prevent window reload
      })).unwrap();
      
      console.log('New appointment created successfully:', newAppointmentResult);
      
      // Exit editing mode after successful save
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving medical data:', err);
      setError(err.message || 'Failed to save medical data');
    } finally {
      setIsSaving(false);
    }
  }

  const toggleEditing = () => {
    setIsEditing(!isEditing)
  }

  // Function to render selected teeth numbers in a more readable format
  const renderSelectedTeeth = () => {
    const teethToShow = isEditing ? localTeethList : (selectedOperation.teeth_list || []);
    
    if (!teethToShow.length) {
      return <span className={styles.no_teeth_selected}>{t('no_teeth_selected') || 'No teeth selected'}</span>;
    }
    
    // Sort teeth by number for better readability
    const sortedTeeth = [...teethToShow].sort((a, b) => a - b);
    return (
      <div className={styles.selected_teeth_list}>
        {sortedTeeth.map(tooth => (
          <span key={tooth} className={styles.tooth_number}>
            {tooth}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.organ_data_main_container}>
      <div className={styles.header_container}>
        <div className={styles.header}>
          <div className={styles.header_actions}>
            <button
              type="button"
              className={`${styles.edit_button} ${isEditing ? styles.editing : ''}`} 
              onClick={toggleEditing}
              disabled={isSaving}
            >
              <EditIcon className={styles.edit_icon} />
            </button>
            {isEditing && (
              <>
                <button 
                  type="button"
                  className={styles.save_button}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : t('save')}
                </button>
                <button 
                  type="button"
                  className={styles.save_as_new_button}
                  onClick={handleSaveAsNew}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : t('save_as_new')}
                </button>
              </>
            )}
          </div>
          <h3 className={styles.text}>
            {groupedTeethList.length > 0 ? (t('grouped_teeth_view') || 'Grouped Teeth View') : t('pt_medical_file')} {isEditing ? '(Editing)' : ''} {isSaving ? '(Saving...)' : ''}
          </h3>
        </div>
      </div>

      {error && (
        <div className={styles.error_message}>
          {error}
        </div>
      )}

      {/* Teeth selection area - Displayed for stomatology patients or when showing grouped teeth */}
      {(isStomatologyPatient || groupedTeethList.length > 0) && (
        <div className={styles.teeth_selection_area}>
          <div className={styles.teeth_selection_header}>
            <div className={styles.segmented_tabs}>
              <button
                type="button"
                className={`${styles.segmented_tab} ${selectedType === 'all' ? styles.active : ''}`}
                onClick={() => setSelectedType('all')}
                title="Trego të gjitha dhëmbët"
              >
                <span role="img" aria-label="Të gjitha">🦷</span>
                <span className={styles.tab_label}>Të gjitha</span>
              </button>
              <button
                type="button"
                className={`${styles.segmented_tab} ${selectedType === 'adult' ? styles.active : ''}`}
                onClick={() => setSelectedType('adult')}
                title="Trego vetëm dhëmbët e rriturve"
              >
                <span role="img" aria-label="Rritur">👨‍🦰</span>
                <span className={styles.tab_label}>Rritur</span>
              </button>
              <button
                type="button"
                className={`${styles.segmented_tab} ${selectedType === 'child' ? styles.active : ''}`}
                onClick={() => setSelectedType('child')}
                title="Trego vetëm dhëmbët e fëmijëve"
              >
                <span role="img" aria-label="Fëmijë">🧒</span>
                <span className={styles.tab_label}>Fëmijë</span>
              </button>
            </div>
            <div className={styles.selected_teeth_summary}>
              <div className={styles.teeth_summary_label}>
                {groupedTeethList.length > 0 ? (t('all_teeth_from_group') || 'All teeth from group') : (t('teeth_selection') || 'Selected Teeth')}:
              </div>
              {groupedTeethList.length > 0 ? (
                <div className={styles.grouped_teeth_display}>
                  <div className={styles.selected_teeth_list}>
                    {groupedTeethList.map(tooth => (
                      <span key={tooth} className={styles.tooth_number}>
                        {tooth}
                      </span>
                    ))}
                  </div>
                  <button
                    className={styles.clear_grouped_teeth_button}
                    onClick={() => {
                      setGroupedTeethList([])
                      if (setShowGroupedTeeth) {
                        setShowGroupedTeeth(null)
                      }
                    }}
                    title={t('clear_grouped_teeth') || 'Clear grouped teeth view'}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                renderSelectedTeeth()
              )}
            </div>
          </div>
          <div className={`${styles.teeth_chart_container} ${isEditing ? styles.editing : ''}`}>
            <SVGMouth
              selectedType={selectedType} 
              selectedTeeth={groupedTeethList.length > 0 ? groupedTeethList : (isEditing ? localTeethList : selectedOperation.teeth_list)} 
              onToothclick={onToothclick}
              groupedTeethInfo={groupedTeethList.length > 0 ? showGroupedTeeth : null}
            />
            {isEditing && (
              <div className={styles.teeth_editing_help}>
                {t('click_teeth_to_select') || 'Click on teeth to select/deselect them'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Issue - Shown for all patients */}
      {currentOperation && (
        <div className={styles.pt_feedback_full_container}>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('main_issue')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={mainIssueRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('main_issue')}
              onBlur={(e) => handleContentChange('main_issue', e.target.innerText)}
            >
              {isEditing ? localContent.main_issue : (selectedOperation.main_issue || selectedOperation.description || t('no_desc_provided'))}
            </div>
          </div>
        </div>
      )}

      {isStomatologyPatient && currentOperation && (
        <>
          <div className={styles.pt_feedback_full_container}>
            <div className={styles.pt_feedback}>
              <label className={styles.feedback_type}>{t('feedback')}</label>
              <div
                className={styles.feedback_readonly}
                contentEditable={isEditing}
                suppressContentEditableWarning={true}
                ref={feedbackRef}
                tabIndex={isEditing ? 0 : -1}
                style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
                aria-label={t('feedback')}
                onBlur={(e) => handleContentChange('feedback', e.target.innerText)}
              >
                {isEditing ? localContent.feedback : (selectedOperation.feedback || t('no_feedback_given'))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Medical Examination Fields - Only show if not stomatology and operation is selected */}
      {!isStomatologyPatient && currentOperation && (
        <div className={styles.pt_feedback_full_container}>
          <h4 className={styles.section_title}>{t('medical_examinations')}</h4>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('examination')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={examinationRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('examination')}
              onBlur={(e) => handleContentChange('examination', e.target.innerText)}
            >
              {isEditing ? localContent.examination : (selectedOperation.examination || t('no_examination'))}
            </div>
          </div>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('rtg_pa_pulmo')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={rtgRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('rtg_pa_pulmo')}
              onBlur={(e) => handleContentChange('rtg_pa_pulmo', e.target.innerText)}
            >
              {isEditing ? localContent.rtg_pa_pulmo : (localContent.rtg_pa_pulmo || t('no_examination'))}
            </div>
          </div>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('ekg')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={ekgRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('ekg')}
              onBlur={(e) => handleContentChange('ekg', e.target.innerText)}
            >
              {isEditing ? localContent.ekg : (localContent.ekg || t('no_examination'))}
            </div>
          </div>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('ta')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={taRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('ta')}
              onBlur={(e) => handleContentChange('ta', e.target.innerText)}
            >
              {isEditing ? localContent.ta : (localContent.ta || t('no_examination'))}
            </div>
          </div>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('spo2')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={spo2Ref}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('spo2')}
              onBlur={(e) => handleContentChange('spo2', e.target.innerText)}
            >
              {isEditing ? localContent.spo2 : (localContent.spo2 || t('no_examination'))}
            </div>
          </div>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('dg')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={dgRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('dg')}
              onBlur={(e) => handleContentChange('dg', e.target.innerText)}
            >
              {isEditing ? localContent.dg : (localContent.dg || t('no_examination'))}
            </div>
          </div>
        </div>
      )}

      {currentOperation && (
        <div className={styles.pt_feedback_full_container} key={selectedOperation.init_date}>
          <div className={styles.pt_feedback}>
            <label className={styles.feedback_type}>{t('therapy')}</label>
            <div
              className={styles.feedback_readonly}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              ref={therapyRef}
              tabIndex={isEditing ? 0 : -1}
              style={isEditing ? { outline: '2px solid #1976d2', background: '#fff' } : {}}
              aria-label={t('therapy')}
              onBlur={(e) => handleContentChange('therapy', e.target.innerText)}
            >
              {isEditing ? localContent.therapy : (selectedOperation.therapy || t('no_therapy'))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrganData
